import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import DashboardLayout from './pages/DashboardLayout';
import SetupWizardPage from './pages/SetupWizardPage';
import { getSetupState, getBackendUrl } from './api';

const V5App = React.lazy(() => import('./v5/V5App'));

function LoadingScreen({ message }) {
  return (
    <div className="app-shell min-h-[100dvh] flex items-center justify-center px-4">
      <div className="app-panel-elevated flex flex-col items-center gap-3 px-8 py-8 text-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--accent)" }} />
        <p className="text-[var(--text-muted)] text-xs font-mono tracking-[0.18em] uppercase">{message}</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen message="Authenticating" />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/**
 * SetupGuard — wraps the dashboard and redirects to /setup if setup is incomplete.
 * Only checks when a backend URL is configured; skips silently if no backend.
 */
function SetupGuard({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user || !getBackendUrl()) {
      setChecked(true);
      return;
    }
    getSetupState()
      .then(r => {
        if (!r.data.completed) navigate('/setup', { replace: true });
      })
      .catch(() => {}) // don't block the dashboard if the call fails
      .finally(() => setChecked(true));
  }, [user, navigate]);

  if (!checked) return <LoadingScreen message="Checking setup" />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen message="Initializing" />;
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Pre-auth setup wizard — configure backend URL before logging in */}
      <Route path="/bootstrap" element={<SetupWizardPage />} />

      {/* V5.0 Autonomous AI Agency — primary authenticated UI */}
      <Route
        path="/v5/*"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingScreen message="Loading the Agency" />}>
              <V5App />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Legacy v4 dashboard — kept for rollback; accessible at /legacy */}
      <Route
        path="/legacy/*"
        element={
          <ProtectedRoute>
            <SetupGuard>
              <DashboardLayout />
            </SetupGuard>
          </ProtectedRoute>
        }
      />

      {/* Default: redirect authenticated users to the Agency v5 */}
      <Route
        path="/*"
        element={user ? <Navigate to="/v5" replace /> : <Navigate to="/login" replace />}
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
